import { useEffect, useState, type FormEvent } from "react";
import { authClient } from "./client";
import { friendlyAuthError } from "../ui/authErrors";
import { normalizeEmail } from "./AuthGate";
import { authStyles as styles } from "./authStyles";

export default function AuthReset() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("error") === "invalid_token") {
      setError("Reset link is invalid or already used. Request a new one.");
    }
  }, []);

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { error: err } = await authClient.requestPasswordReset({
        email: normalizeEmail(email),
        redirectTo: `${window.location.origin}/auth/reset`,
      });
      if (err) {
        setError(friendlyAuthError(err, "Could not send reset email."));
      } else {
        setRequested(true);
      }
    } catch (ex: unknown) {
      setError(friendlyAuthError(ex, "Could not send reset email."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const cleaned = password.trim();
    if (cleaned !== confirmPassword.trim()) {
      setError("Passwords don't match.");
      return;
    }
    if (cleaned.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await authClient.resetPassword({
        newPassword: cleaned,
        token: token!,
      });
      if (err) {
        setError(friendlyAuthError(err, "Could not reset password."));
      } else {
        window.location.href = "/?reset=success";
      }
    } catch (ex: unknown) {
      setError(friendlyAuthError(ex, "Could not reset password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logo}>
            <svg viewBox="0 0 12 12" fill="none" width="14" height="14">
              <path d="M2 9L6 2L10 9H2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={styles.brand}>CAD-PLM</span>
        </div>

        {token ? (
          <>
            <h2 style={styles.heading}>Set a new password</h2>
            <form onSubmit={handleSetPassword} style={styles.form}>
              <input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                style={styles.input}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                style={styles.input}
                data-testid="confirm-password"
              />
              {error && <div style={styles.error}>{error}</div>}
              <button type="submit" disabled={submitting} style={styles.primaryBtn}>
                {submitting ? "Please wait..." : "Set password"}
              </button>
            </form>
          </>
        ) : requested ? (
          <>
            <h2 style={styles.heading}>Check your email</h2>
            <p style={styles.body}>
              If an account exists for that email, a reset link is on its way. The link expires in 1 hour.
            </p>
            <p style={styles.body}>
              <a href="/" style={styles.toggleBtn}>← Back to sign in</a>
            </p>
          </>
        ) : (
          <>
            <h2 style={styles.heading}>Reset your password</h2>
            <form onSubmit={handleRequest} style={styles.form}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                style={styles.input}
              />
              {error && <div style={styles.error}>{error}</div>}
              <button type="submit" disabled={submitting} style={styles.primaryBtn}>
                {submitting ? "Sending..." : "Send reset link"}
              </button>
            </form>
            <p style={styles.body}>
              <a href="/" style={styles.toggleBtn}>← Back to sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

